import { BaseText, Meta, theme } from 'aimo-ui';
import SelectableModalContentItem from 'components/common/SelectableModalContentItem';
import SessionAvatar from 'components/common/SessionAvatar';
import useDurationFromDate from 'hooks/useDurationFromDate';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/native';
import {
  ChargingSession,
  ChargingStatus,
  Maybe,
  ParkingSession,
  Vehicle,
} from 'types/generatedSchemaTypes';
import { isItemParking } from 'utils/parkUtils';
import { getDurationFromDates, getTimeLeft } from 'utils/timeUtils';

const Column = styled.View`
  flex-direction: column;
  width: 100%;
`;

const Row = styled.View`
  flex-direction: row;
`;

const TextWrapper = styled.View`
  flex-shrink: 1;
`;

const OngoingMultipleSessionItem = ({
  session,
  id = '',
  handlePress,
  currentTime,
  vehicles,
  megaflexValue,
}: {
  session: ParkingSession | ChargingSession;
  handlePress: (item: ParkingSession | ChargingSession) => void;
  id?: Maybe<string>;
  currentTime: Date;
  vehicles: Maybe<Vehicle[]> | undefined;
  megaflexValue: string | undefined;
}) => {
  const isParking = isItemParking(session);
  const { t } = useTranslation();

  const startDate =
    isParking && session?.type === 'ANPR'
      ? new Date(session?.startDate as Date)
      : null;

  const duration = useDurationFromDate(startDate, 30000, [
    'days',
    'hours',
    'minutes',
  ]);

  const getVehicleInfo = () => {
    const licensePlate = isParking
      ? session?.accessDevice?.value
      : session?.licensePlate;
    return vehicles?.find(
      (vehicle) => vehicle.licensePlate.plateText === licensePlate
    )?.licensePlate.plateText;
  };

  const getParkingTime = () => {
    if (!isParking) return '';
    if (session?.type === 'ANPR') {
      return duration;
    }
    const { overdue, timeLeft } = getTimeLeft(
      new Date(session?.predefinedEndDate as string),
      currentTime
    );
    return t(overdue ? 'time.overdue' : 'time.remaining', { timeLeft });
  };

  const getChargingTime = () => {
    if (isParking) return '';
    if (!session?.startTime) return '...';
    const duration = getDurationFromDates(
      new Date(session.startTime),
      currentTime,
      ['hours', 'minutes']
    );
    return duration === '' ? '...' : duration;
  };

  const getSessionTitle = () => {
    if (isParking) return t('parking.ongoingTitle');
    switch (session?.status as ChargingStatus) {
      case 'START_IN_QUEUE':
        return t('evCharging.starting');
      case 'STOP_IN_QUEUE':
        return t('evCharging.stopping');
      case 'IN_PROGRESS':
      default:
        return t('evCharging.charging');
    }
  };

  return (
    <SelectableModalContentItem
      isSelected={false}
      disabled={false}
      onPress={() => handlePress(session)}
      testID={`active-session-wrap-${id}`}
      iconNode={<SessionAvatar session={session} />}
      paddingRight={0}>
      <Column>
        <Row>
          <BaseText>{getSessionTitle()}</BaseText>
          <BaseText color={theme.colors.gray300}>{' • '}</BaseText>
          <BaseText>
            {isParking ? getParkingTime() : getChargingTime()}
          </BaseText>
        </Row>
        <Row>
          <TextWrapper>
            <Meta
              testID={`ongoing-session-${id}-parkzone-name`}
              color={theme.colors.gray300}
              numberOfLines={1}>
              {session?.parkZone?.name}
            </Meta>
          </TextWrapper>
          <Meta color={theme.colors.gray300}>{' • '}</Meta>
          <Meta
            testID={`ongoing-session-${id}-license-plate`}
            color={theme.colors.gray300}>
            {getVehicleInfo() ?? megaflexValue ?? ''}
          </Meta>
        </Row>
      </Column>
    </SelectableModalContentItem>
  );
};

export default OngoingMultipleSessionItem;
